import { snakeCase } from "lodash";
import React, { ElementType, Fragment, FunctionComponent } from "react";
import { Button, Dropdown } from "react-bootstrap";
import Icon from "./Icon";
import { Tooltip } from "./tooltip";

type IActionItem = {
  label?: string;
  widget?: object | null;
  onClick?: (data: unknown) => void;
  href?: string;
  disabled?: boolean;
  disabledMessage?: string | null;
  divider?: boolean;
  header?: boolean;
};

const renderMenuItem = (
  idx: number,
  menuItem: React.ReactElement,
  tooltipMessage?: string
) =>
  !tooltipMessage ? (
    <Fragment key={idx}>{menuItem}</Fragment>
  ) : (
    <Tooltip
      key={`overlay-${menuItem.key}`}
      id="menu-action-warning-tooltip"
      className="menu-action-tooltip"
      content={tooltipMessage}
      placement="top"
    >
      {menuItem}
    </Tooltip>
  );

const getMenuItem = (action: IActionItem, idx: number): React.ReactElement => {
  if (action.divider) {
    return <Dropdown.Divider key={idx} />;
  } else if (action.header) {
    return (
      <Dropdown.Header className="actions-menu__header">
        <>{action.label}</>
      </Dropdown.Header>
    );
  } else {
    return (
      <Dropdown.Item
        key={idx}
        eventKey={idx}
        onClick={action.onClick}
        href={action.href}
        disabled={action.disabled}
        data-testid={`action_${snakeCase(action.label)}`}
      >
        <>
          {action.label} {action.widget}
        </>
      </Dropdown.Item>
    );
  }
};

interface IActionMenuProps {
  id: string;
  title?: string;
  buttonClassName?: string;
  buttonComponent?: ElementType;
  className?: string;
  disabled?: boolean;
  disabledActionDefaultMessage?: string;
  showDropdownIcon?: boolean;
  stopPropagationOnClick?: boolean;
  onToggle?: (isShown: boolean) => void;
  sendSkipRowClickEvent?: boolean;
  variant?: string;
  actions?: IActionItem[];
  useFixedPosition?: boolean;
}

const ActionsMenu: FunctionComponent<IActionMenuProps> = ({
  id,
  actions = [],
  buttonClassName,
  buttonComponent = Button,
  className = "",
  disabled = false,
  disabledActionDefaultMessage,
  sendSkipRowClickEvent,
  stopPropagationOnClick = true,
  showDropdownIcon = false,
  title,
  variant = "link",
  onToggle,
  useFixedPosition = false
}) => (
  <div
    className={`rlbs actions-menu ${className}`}
    onClick={e => {
      if (stopPropagationOnClick) {
        e.stopPropagation();
      } else if (sendSkipRowClickEvent) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        e.target.skipRowClick = true;
      }
    }}
  >
    <Dropdown
      onToggle={onToggle}
      className={`${buttonClassName ?? "btn--icon-only btn--no-hover"}`}
    >
      <Dropdown.Toggle
        as={buttonComponent}
        id={`actions-dropdown-${id}`}
        data-testid={snakeCase(`actions_dropdown_${id}`)}
        disabled={disabled}
        variant={variant || "link"}
      >
        {title || <Icon name="menu" />}
        {showDropdownIcon ? (
          <Icon name="chevron-down" className="dropdown-toggle__chevron-icon" />
        ) : null}
      </Dropdown.Toggle>

      <Dropdown.Menu
        renderOnMount
        align="end"
        data-testid={snakeCase(`actions_menu_${id}`)}
        popperConfig={
          useFixedPosition
            ? {
                strategy: "fixed",
                modifiers: [
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "viewport"
                    }
                  }
                ]
              }
            : undefined
        }
      >
        {actions.map((action, idx) => {
          const disabledMessage =
            action.disabledMessage || disabledActionDefaultMessage;
          return renderMenuItem(
            idx,
            getMenuItem(action, idx),
            action.disabled ? disabledMessage : undefined
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  </div>
);

export default ActionsMenu;
